import { jsPDF } from 'jspdf';
import type { Vessel, Survey } from '../types';
import 'jspdf-autotable';

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function generateSurveyPDF(
  vessel: Vessel,
  survey: Survey,
  details: string[],
  _warnings: string[]
): Blob {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 20;

  // Header
  doc.setFontSize(18);
  doc.setTextColor(0, 51, 102);
  doc.text('DRAFT SURVEY REPORT', pageWidth / 2, y, { align: 'center' });
  y += 10;

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Vessel: ${vessel.name} | LBP: ${vessel.lbp}m | Survey: ${survey.survey_type.toUpperCase()}`, pageWidth / 2, y, { align: 'center' });
  y += 8;
  doc.text(`Date: ${new Date(survey.survey_date).toLocaleString()} | Method: ${survey.method.toUpperCase()}`, pageWidth / 2, y, { align: 'center' });
  y += 15;

  // Weather / Conditions (placeholder section)
  doc.setFontSize(12);
  doc.setTextColor(0, 51, 102);
  doc.text('SURVEY CONDITIONS', margin, y);
  y += 8;
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text('Weather: _____________________    Sea State: _____________________    Current: _____________________', margin, y);
  y += 12;

  // Draft Readings Table
  doc.setFontSize(12);
  doc.setTextColor(0, 51, 102);
  doc.text('DRAFT READINGS', margin, y);
  y += 8;

  const drafts = survey.drafts || { fwd_port: 0, fwd_stbd: 0, mid_port: 0, mid_stbd: 0, aft_port: 0, aft_stbd: 0, density: 1.025 };
  const density = drafts.density ?? 1.025;
  const draftData = [
    ['Station', 'Port (m)', 'Starboard (m)', 'Mean (m)'],
    ['Forward', drafts.fwd_port.toFixed(3), drafts.fwd_stbd.toFixed(3), ((drafts.fwd_port + drafts.fwd_stbd) / 2).toFixed(3)],
    ['Midship', drafts.mid_port.toFixed(3), drafts.mid_stbd.toFixed(3), ((drafts.mid_port + drafts.mid_stbd) / 2).toFixed(3)],
    ['Aft', drafts.aft_port.toFixed(3), drafts.aft_stbd.toFixed(3), ((drafts.aft_port + drafts.aft_stbd) / 2).toFixed(3)],
  ];

  (doc as any).autoTable({
    startY: y,
    head: [draftData[0]],
    body: draftData.slice(1),
    theme: 'grid',
    headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255], fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    margin: { left: margin, right: margin },
  });
  y = (doc as any).lastAutoTable.finalY + 12;

  // Water Density
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(`Water Density: ${density.toFixed(4)} t/m³`, margin, y);
  y += 12;

  // Calculation Log
  doc.setFontSize(12);
  doc.setTextColor(0, 51, 102);
  doc.text('CALCULATION LOG', margin, y);
  y += 8;

  doc.setFontSize(8);
  doc.setTextColor(40, 40, 40);
  for (const line of details) {
    if (y > pageHeight - 30) {
      doc.addPage();
      y = 20;
    }
    const isHighlight = line.includes('TOTAL') || line.includes('CARGO') || line.includes('Final') || line.includes('Quarter Mean') || line.includes('ALWAYS') || line.includes('δMTC');
    if (isHighlight) {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 100, 150);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(40, 40, 40);
    }
    doc.text(line, margin, y);
    y += 5;
  }
  y += 8;

  // Displacement Result
  if (y > pageHeight - 40) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 51, 102);
  doc.text('RESULTS', margin, y);
  y += 12;

  doc.setFontSize(11);
  doc.text(`Corrected Displacement: ${(survey.displacement ?? 0).toFixed(3)} MT`, margin, y);
  y += 8;

  // Constants / Deductions Table
  if (survey.constants) {
    doc.setFontSize(12);
    doc.setTextColor(0, 51, 102);
    doc.text('DEDUCTIONS (Constants)', margin, y);
    y += 8;

    const constData = [
      ['Item', 'Weight (MT)'],
      ['Bunkers (Fuel Oil)', (survey.constants.bunkers || 0).toFixed(2)],
      ['Fresh Water', (survey.constants.fw || 0).toFixed(2)],
      ['Ballast Water', (survey.constants.ballast || 0).toFixed(2)],
      ['Stores & Provisions', (survey.constants.stores || 0).toFixed(2)],
      ['Slops', (survey.constants.slops || 0).toFixed(2)],
      ['Others', (survey.constants.others || 0).toFixed(2)],
      ['TOTAL DEDUCTIONS', (
        (survey.constants.bunkers || 0) +
        (survey.constants.fw || 0) +
        (survey.constants.ballast || 0) +
        (survey.constants.stores || 0) +
        (survey.constants.slops || 0) +
        (survey.constants.others || 0)
      ).toFixed(2)],
    ];

    (doc as any).autoTable({
      startY: y,
      head: [constData[0]],
      body: constData.slice(1),
      theme: 'grid',
      headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255], fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      margin: { left: margin, right: margin },
    });
    y = (doc as any).lastAutoTable.finalY + 12;
  }

  // Net Displacement
  const totalDeductions =
    (survey.constants?.bunkers || 0) +
    (survey.constants?.fw || 0) +
    (survey.constants?.ballast || 0) +
    (survey.constants?.stores || 0) +
    (survey.constants?.slops || 0) +
    (survey.constants?.others || 0);
  const netDisplacement = (survey.displacement ?? 0) - totalDeductions;
  if (Number.isNaN(netDisplacement)) {
    doc.text('Error: Invalid displacement calculation', margin, y);
    y += 8;
  }

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 0, 0);
  doc.text(`NET DISPLACEMENT: ${netDisplacement.toFixed(3)} MT`, margin, y);
  y += 15;

  // Signatures
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  doc.text('Surveyor Signature: _________________________    Date: _______________', margin, y);
  y += 8;
  doc.text('Master Signature: _________________________    Date: _______________', margin, y);
  y += 8;
  doc.text('Witness Signature: ________________________    Date: _______________', margin, y);
  y += 15;

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text('Generated by Draft Survey Pro | COMANAV Agent Tool', pageWidth / 2, pageHeight - 10, { align: 'center' });

  return doc.output('blob');
}

export function generateCargoReportPDF(
  vessel: Vessel,
  initial: Survey,
  final: Survey,
  cargoWeight: number,
  _details: string[],
  _warnings: string[]
): Blob {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 20;

  // Header
  doc.setFontSize(18);
  doc.setTextColor(0, 51, 102);
  doc.text('CARGO SURVEY REPORT', pageWidth / 2, y, { align: 'center' });
  y += 10;

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Vessel: ${vessel.name} | LBP: ${vessel.lbp}m`, pageWidth / 2, y, { align: 'center' });
  y += 8;
  doc.text(`Cargo Weight: ${(cargoWeight || 0).toFixed(3)} MT`, pageWidth / 2, y, { align: 'center' });
  y += 15;

  // Initial / Final Summary Table
  doc.setFontSize(12);
  doc.setTextColor(0, 51, 102);
  doc.text('SURVEY SUMMARY', margin, y);
  y += 8;

  const summaryData = [
    ['Parameter', 'Initial Survey', 'Final Survey', 'Difference'],
    ['Displacement (MT)', (initial.displacement || 0).toFixed(3), (final.displacement || 0).toFixed(3), ((final.displacement || 0) - (initial.displacement || 0)).toFixed(3)],
    ['Bunkers (MT)', (initial.constants?.bunkers || 0).toFixed(2), (final.constants?.bunkers || 0).toFixed(2), ((final.constants?.bunkers || 0) - (initial.constants?.bunkers || 0)).toFixed(2)],
    ['Fresh Water (MT)', (initial.constants?.fw || 0).toFixed(2), (final.constants?.fw || 0).toFixed(2), ((final.constants?.fw || 0) - (initial.constants?.fw || 0)).toFixed(2)],
    ['Ballast (MT)', (initial.constants?.ballast || 0).toFixed(2), (final.constants?.ballast || 0).toFixed(2), ((final.constants?.ballast || 0) - (initial.constants?.ballast || 0)).toFixed(2)],
    ['Stores (MT)', (initial.constants?.stores || 0).toFixed(2), (final.constants?.stores || 0).toFixed(2), ((final.constants?.stores || 0) - (initial.constants?.stores || 0)).toFixed(2)],
    ['Slops (MT)', (initial.constants?.slops || 0).toFixed(2), (final.constants?.slops || 0).toFixed(2), ((final.constants?.slops || 0) - (initial.constants?.slops || 0)).toFixed(2)],
    ['Others (MT)', (initial.constants?.others || 0).toFixed(2), (final.constants?.others || 0).toFixed(2), ((final.constants?.others || 0) - (initial.constants?.others || 0)).toFixed(2)],
  ];

  (doc as any).autoTable({
    startY: y,
    head: [summaryData[0]],
    body: summaryData.slice(1),
    theme: 'grid',
    headStyles: { fillColor: [0, 51, 102], textColor: [255, 255, 255], fontSize: 9 },
    bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
    margin: { left: margin, right: margin },
  });
  y = (doc as any).lastAutoTable.finalY + 15;

  // Cargo Result
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(180, 0, 0);
  doc.text(`CARGO WEIGHT: ${cargoWeight.toFixed(3)} MT`, margin, y);
  y += 15;

  // Signatures
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont('helvetica', 'normal');
  doc.text('Surveyor Signature: _________________________    Date: _______________', margin, y);
  y += 8;
  doc.text('Master Signature: _________________________    Date: _______________', margin, y);
  y += 8;
  doc.text('Witness Signature: ________________________    Date: _______________', margin, y);
  y += 15;

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text('Generated by Draft Survey Pro | COMANAV Agent Tool', pageWidth / 2, pageHeight - 10, { align: 'center' });

  return doc.output('blob');
}
